import { pick } from 'lodash-es';
import { deepmerge } from '@shared/utils/deepmerge';

/**
 * Normalizes a partial data object by selecting specific fields and merging them with fallback values.
 *
 * Ensures that only the specified fields from the fallback object are preserved, and values from `data`
 * take precedence when present. This is especially useful for standardizing inputs from external sources,
 * like API responses, while enforcing structural and type safety in the result.
 *
 * Note: Only fields present in the `fallback` object will be considered during normalization.
 * Deep merging is applied to nested objects and arrays, based on the merge strategy provided.
 *
 * @template Data - The input data object type (e.g., a partial or external response object).
 * @template Fallback - The fallback object type, which defines the structure and defaults for normalization.
 *
 * @param data - The input object potentially containing some of the expected fields.
 * @param fallback - A complete object of type `Fallback` that provides default values for normalization.
 * @param options - Optional configuration for deep merging. You can control array behavior via:
 *                  - `'source'`: use arrays from `data` if available
 *                  - `'target'`: use arrays from `fallback` (default)
 *                  - `'combine'`: merge arrays from both
 *
 * @returns A normalized object of type `Fallback`, with values from `data` overriding `fallback` where applicable.
 *
 * @example
 * const data = {
 *   id: '1',
 *   addressComponents: [{ languageCode: 'en', longText: 'The Westin Pune Koregaon Park' }]
 * };
 *
 * const fallback = {
 *   id: '',
 *   addressComponents: [{ languageCode: '', longText: '', shortText: '', types: [] }]
 * };
 *
 * const result = normalizeWithFallback(data, fallback, { arrayMerge: 'target' });
 * // result: {
 * //   id: '1',
 * //   addressComponents: [
 * //     {
 * //       languageCode: 'en',
 * //       longText: 'The Westin Pune Koregaon Park',
 * //       shortText: '',
 * //       types: []
 * //     }
 * //   ]
 * // }
 */
export const normalizeWithFallback = <Data extends object, Fallback extends Partial<Fallback>>(
  data: Data,
  fallback: Fallback,
  options?: { arrayMerge?: 'source' | 'target' | 'combine' },
): Fallback => {
  const fields = Object.keys(fallback) as (keyof Fallback)[];
  const pickedInput = pick(data, fields);
  const pickedFallback = pick(fallback, fields);
  return deepmerge(pickedFallback, pickedInput, options);
};
